/**
 * A screen that loads its code the first time it is shown. While it loads, the section shows the
 * usual loading spinner; nothing else on the page waits for it. Props pass through unchanged.
 * `preload()` starts the download early (Shell does this for the section the address opens), and
 * a screen whose code is already here draws directly: no spinner and no extra wait before its
 * own data requests start. If the download fails (offline, a dropped connection), the section says
 * so with Try again instead of the whole app going blank. The browser remembers a failed module
 * download for the life of the page, so Try again reloads the page at the same address (the
 * browser still asks first when something isn't saved).
 */
import { Suspense, lazy, useState, type ComponentType } from 'react';
import { useI18n } from '../lib/i18n.tsx';
import { Banner, Button, Spinner } from '../ui/ui.tsx';
import { loadOnce } from './loadOnce.ts';

function Loading() {
  const { t } = useI18n();
  return <Spinner label={t('state.loading')} />;
}

function LoadFailed() {
  const { t } = useI18n();
  return <div className="center pad"><Banner tone="attention" action={<Button onClick={() => location.reload()}>{t('state.retry')}</Button>}>{t('screen.loadFailed')}</Banner></div>;
}

export type LazyView<P> = ComponentType<P> & { preload: () => Promise<unknown> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyView<P extends Record<string, any>>(load: () => Promise<ComponentType<P>>): LazyView<P> {
  const { preload, loaded } = loadOnce(load);
  const Screen = lazy(async () => {
    try { return { default: await preload() }; }
    catch { return { default: LoadFailed as unknown as ComponentType<P> }; }
  });
  const View = (props: P) => {
    // Chosen once per mount, so a screen never switches between the two ways while open.
    const [Ready] = useState(loaded);
    return Ready ? <Ready {...props} /> : <Suspense fallback={<Loading />}><Screen {...props} /></Suspense>;
  };
  return Object.assign(View, { preload });
}
